import type {
  LeafPrimitive,
  PrinterProfile,
  SymbolComponent,
  SymbolDataStructure,
  Template,
  TextValue,
  Warning,
  Zone,
} from '@tsagko/label-contracts'
import { flattenZones, type FlatZone } from './flatten-zones'
import { validateEan13 } from './barcodes/ean-13'
import { validateItf14 } from './barcodes/itf-14'
import { projectPlacement } from './place-baked-instance'
import { splitChildBoxes } from './split'

/**
 * Legacy `ValueSource` shape. See `internal/units.ts` for the full
 * compatibility story.
 */
type LegacyStatic = { kind: 'static'; value: string }
type LegacyBound = { kind: 'bound'; fieldKey: string }
type AnyValueSource = TextValue | LegacyStatic | LegacyBound

/**
 * Regex matching a TextValue content string that is a single `{name}`
 * reference — or a bare `{}` empty-name reference, which the schema forbids
 * at save time (see `checkBound` below) but which hand-constructed test
 * fixtures and pre-migration data can still carry. The capture group is
 * OPTIONAL (unlike `variables.ts#SINGLE_VARIABLE_RE`, which is intentionally
 * strict since it drives render-time resolution): `checkBound` relies on
 * `match[1] === undefined` to detect the empty-name case.
 */
const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)?\}\s*$/

/**
 * If `source` is a "static" / pure-literal text value, return the literal
 * string. Otherwise (a `{name}` reference, or a legacy `{ kind: 'bound' }`
 * shape) return `undefined`. Used by lint checks that can only validate
 * statically known payloads (e.g. EAN-13 check-digit verification).
 */
function extractStaticLiteral(source: AnyValueSource): string | undefined {
  if ('content' in source) {
    return SINGLE_VARIABLE_RE.test(source.content) ? undefined : source.content
  }
  return source.kind === 'static' ? source.value : undefined
}

/**
 * Floating-point slack used by the bounds/clipping comparisons below. Hoisted
 * to the top of the module so it appears before its first use in
 * `lintZoneBounds` (the const works either way thanks to JS hoisting, but
 * top-down readability matters in a lint file with several inter-dependent
 * helpers).
 */
const EPS = 1e-6

/**
 * Phase 1 lint surface (sub-task 2a.10.15).
 *
 * Walks `flattenZones(template)` (so baked-block expansions are linted
 * alongside top-level zones) and emits a `Warning[]` covering the Phase 1
 * warning codes. The walk is read-only — `lintTemplate` does not call
 * `buildCommands`, does not resolve images, and does not need a data record.
 *
 * Phase 1 warning codes emitted:
 *
 *   - `CLIPPING` (severity `warn`):
 *     A flattened zone extends beyond `template.widthMm`/`heightMm`, OR
 *     beyond `profile.usablePrintWidthMm` when a `profile` is provided.
 *     "Extends beyond" means right-edge > template width or bottom-edge >
 *     template height (we ignore negative origins here — those are
 *     OUT_OF_BOUNDS).
 *
 *   - `EMPTY_ZONE` (severity `error`):
 *     A zone object that has neither `split`/`children` nor `component`.
 *     `flattenZones` only emits leaf zones with a component, so this is
 *     detected with a separate recursive walk of `template.rootZone`. The
 *     Zod refine on ZoneSchema usually blocks this, but lint catches
 *     hand-constructed templates that bypassed validation (which the tests
 *     exercise via `as Template`).
 *
 *   - `OUT_OF_BOUNDS` (severity `error`):
 *     A leaf with `absolutePosition` where `x<0`, `y<0`, OR
 *     `x + w > template.widthMm` OR `y + h > template.heightMm`.
 *     The bounds check intentionally uses TEMPLATE dims here (not profile
 *     usable width) — CLIPPING covers the profile side.
 *
 *   - `UNBOUND_REQUIRED_FIELD` (severity `warn`):
 *     A `ValueSource: { kind: 'bound', fieldKey: '' }` (empty string). We
 *     check every text component, localized variant, symbol data structure,
 *     image source. Image's `kind: 'field'` with empty `fieldKey` also fires.
 *
 *   - `GTIN_CHECKSUM_MISMATCH` (severity `error`):
 *     EAN-13 component with `dataStructure: { kind: 'gtin', checksum: 'validated' }`
 *     whose value (when statically resolvable, i.e. `value.kind === 'static'`)
 *     is 13 digits with a wrong check digit. For `bound` values we cannot
 *     validate without runtime data, so we skip. The `auto` and `skip`
 *     checksum modes are also skipped — only `validated` triggers this
 *     warning, since the runtime is permissive (sub-task 2a.10.13 carry-over).
 *
 *   - `FIRMWARE_QUIRK` (severity `info`):
 *     Separator component with `style: 'dashed'` or `'dotted'`. Zebra `^GB`
 *     renders solid only; the dashed/dotted styles silently render as solid
 *     in Phase 1. The closest semantic match in the warning enum is
 *     FIRMWARE_QUIRK, reused here.
 *
 *   - `SYMBOLOGY_VALIDATION_FAILED` (severity `warn` / `info`):
 *     A symbology paired with a `dataStructure.kind` that doesn't match the
 *     symbology's semantic shape:
 *       - EAN-13 + `raw`/`url` → expects `gtin`
 *       - Code-128 + `gtin`/`gs1-ai` → expects `raw`/`url`
 *       - QR + `gtin` → unusual (info — QR is permissive)
 *       - GS1-128 + `raw`/`url`/`gtin` → expects `gs1-ai`
 *
 * Deferred (not emitted here):
 *   - Empty-payload silent-skip warnings — these require resolving against
 *     a data record, which `lintTemplate` doesn't accept. Phase 2 work.
 */
export function lintTemplate(
  template: Template,
  profile?: PrinterProfile
): Warning[] {
  const warnings: Warning[] = []

  // EMPTY_ZONE detection walks the raw rootZone tree (flattenZones skips
  // empty zones). We collect every empty leaf — a zone with neither
  // `component` nor `split`/`children`.
  collectEmptyZones(template, warnings)

  // PLACEMENT_OVERFLOW detection walks the raw rootZone tree because the
  // warning path must include the host zone id AND baked instance id as a
  // pair — flattening loses that pairing.
  lintPlacement(template, warnings)

  // The rest of the warnings operate on flattened zones, which already have
  // absolute positions and have expanded baked-block instances. flattenZones
  // throws on cycles; that's a programmer error and should propagate.
  const flat = flattenZones(template)

  for (const zone of flat) {
    lintZoneBounds(zone, template, profile, warnings)
    lintComponent(zone, warnings)
  }

  return warnings
}

/**
 * Recursively walk the zone tree looking for zones that are neither splits
 * (with children) nor leaves (with a component). flattenZones skips these
 * silently (the `if (zone.component !== undefined)` arm at the bottom of
 * walk()), so we have to do an independent traversal to catch them.
 *
 * The Zod refine on ZoneSchema normally blocks this construction, but
 * tests can bypass the schema via `as Template`. In that case lint should
 * surface the structural error rather than letting flattenZones produce a
 * confusing empty result.
 */
function collectEmptyZones(template: Template, warnings: Warning[]): void {
  const visit = (zone: Template['rootZone'], path: string[]): void => {
    const here = [...path, zone.id]
    if (zone.split !== undefined && zone.children !== undefined) {
      visit(zone.children[0], here)
      visit(zone.children[1], here)
      return
    }
    if (zone.component === undefined) {
      warnings.push({
        code: 'EMPTY_ZONE',
        severity: 'error',
        message: `Zone "${zone.id}" has no component and no split — it cannot render.`,
        path: here,
        suggestion: 'Attach a primitive component or remove the zone.',
      })
    }
  }
  visit(template.rootZone, [])
}

/**
 * Emits PLACEMENT_OVERFLOW for any baked-block-instance whose `placement`
 * is larger than its host zone on either axis. Walks `template.rootZone`
 * directly (not the flattened output) because the warning's `path` must
 * include the host zone id AND the baked instance id together — flattening
 * loses that pairing.
 *
 * Recursion: descends into the expansion using `projectPlacement` so any
 * baked instance NESTED inside an expansion is also linted against its
 * (projected) host. Mirror the pipeline's flatten semantics.
 */
function lintPlacement(template: Template, warnings: Warning[]): void {
  function walk(
    zone: Zone,
    box: { x: number; y: number; w: number; h: number },
    path: readonly string[]
  ): void {
    const nextPath = [...path, zone.id]
    if (zone.split !== undefined && zone.children !== undefined) {
      const [a, b] = zone.children
      const [boxA, boxB] = splitChildBoxes(box, zone.split)
      walk(a, boxA, nextPath)
      walk(b, boxB, nextPath)
      return
    }
    if (zone.component?.type === 'baked-block-instance') {
      const p = zone.component.placement
      const hostBox = zone.absolutePosition ?? box
      if (p.widthMm - hostBox.w > EPS || p.heightMm - hostBox.h > EPS) {
        warnings.push({
          code: 'PLACEMENT_OVERFLOW',
          severity: 'error',
          message:
            `baked block ${zone.component.blockId}@v${zone.component.blockVersion}` +
            ` requires ${p.widthMm}×${p.heightMm} mm but host zone is` +
            ` ${hostBox.w}×${hostBox.h} mm`,
          path: [...nextPath, zone.component.id],
        })
      }
      const projected = projectPlacement(hostBox, p)
      walk(zone.component.expansion, projected, [...nextPath, zone.component.id])
    }
  }
  walk(template.rootZone, { x: 0, y: 0, w: template.widthMm, h: template.heightMm }, [])
}

/**
 * Bounds checks for a single flattened leaf. Emits OUT_OF_BOUNDS when the
 * leaf's effective box has a negative origin or extends past the template
 * dims, and CLIPPING when the (non-negative) right/bottom edge crosses
 * template OR profile-usable-width thresholds.
 *
 * The two codes are intentionally separate: OUT_OF_BOUNDS is an `error`
 * (the leaf cannot render in-bounds at all), CLIPPING is a `warn` (the
 * leaf renders but part of it will be cut off by the printer head).
 */
function lintZoneBounds(
  zone: FlatZone,
  template: Template,
  profile: PrinterProfile | undefined,
  warnings: Warning[]
): void {
  const { xMm, yMm, widthMm, heightMm, path } = zone
  // OUT_OF_BOUNDS: negative origin OR right/bottom edge past template dims.
  // We treat the absolutePosition case and the computed-box case
  // identically here — both arrive as the same (xMm, yMm, widthMm, heightMm)
  // tuple in FlatZone.
  if (xMm < 0 || yMm < 0) {
    warnings.push({
      code: 'OUT_OF_BOUNDS',
      severity: 'error',
      message: `Zone has a negative origin (x=${xMm}, y=${yMm}) — it cannot render at this position.`,
      path,
    })
  } else if (
    xMm + widthMm > template.widthMm + EPS ||
    yMm + heightMm > template.heightMm + EPS
  ) {
    warnings.push({
      code: 'OUT_OF_BOUNDS',
      severity: 'error',
      message: `Zone at (${xMm}, ${yMm}) size ${widthMm}×${heightMm}mm extends past template bounds ${template.widthMm}×${template.heightMm}mm.`,
      path,
    })
    // Don't double-emit CLIPPING for the same zone — OUT_OF_BOUNDS supersedes.
    return
  }

  // CLIPPING (template): the leaf is in-bounds for the template but extends
  // past either dimension. This only fires when neither arm of OUT_OF_BOUNDS
  // fired (we returned above on the past-template case). The template-bounds
  // arm of CLIPPING is therefore unreachable in the current model BUT we
  // keep the check as documentation of the intended split: any future
  // refinement (e.g. a soft "approaching edge" threshold) plugs in here.

  // CLIPPING (profile): when a profile is given, check the right edge against
  // its usable print width. Many Zebra printers have a usable width <
  // template width (e.g. 104mm head on a 110mm media).
  if (
    profile !== undefined &&
    xMm + widthMm > profile.usablePrintWidthMm + EPS
  ) {
    warnings.push({
      code: 'CLIPPING',
      severity: 'warn',
      message: `Zone right edge (${(xMm + widthMm).toFixed(2)}mm) exceeds printer usable width (${profile.usablePrintWidthMm}mm) — content will be clipped.`,
      path,
      suggestion: `Reposition the zone or switch to a wider printer profile.`,
    })
  }
}

/**
 * Per-component lint. Dispatches on `component.type` and emits the
 * structural warnings appropriate to each leaf shape:
 *
 *   - `text`               → UNBOUND_REQUIRED_FIELD on value
 *   - `image`              → UNBOUND_REQUIRED_FIELD on source.fieldKey
 *   - `symbol`             → UNBOUND_REQUIRED_FIELD on dataStructure value(s),
 *                            GTIN_CHECKSUM_MISMATCH on EAN-13 validated,
 *                            SYMBOLOGY_VALIDATION_FAILED on shape mismatch
 *   - `separator`          → FIRMWARE_QUIRK on dashed/dotted
 *   - `localized-text` /   → UNBOUND_REQUIRED_FIELD on each variant value
 *     `localized-rich-text`
 */
function lintComponent(zone: FlatZone, warnings: Warning[]): void {
  const c: LeafPrimitive = zone.component
  switch (c.type) {
    case 'text':
      checkBound(c.value, zone.path, `text "${c.id}".value`, warnings)
      return
    case 'image':
      if (c.source.kind === 'field' && c.source.fieldKey === '') {
        warnings.push({
          code: 'UNBOUND_REQUIRED_FIELD',
          severity: 'warn',
          message: `Image "${c.id}" has an empty fieldKey — it will resolve to nothing at print time.`,
          path: zone.path,
          suggestion: 'Set the fieldKey to a non-empty data binding.',
        })
      }
      return
    case 'symbol':
      lintSymbol(zone, c, warnings)
      return
    case 'separator':
      if (c.style === 'dashed' || c.style === 'dotted') {
        warnings.push({
          code: 'FIRMWARE_QUIRK',
          severity: 'info',
          message: `Zebra ^GB renders solid only; dashed/dotted style is rendered as solid in Phase 1.`,
          path: zone.path,
        })
      }
      return
    case 'localized-text':
    case 'localized-rich-text': {
      c.variants.forEach((v, i) => {
        checkBound(
          v.value,
          zone.path,
          `${c.type} "${c.id}".variants[${i}] (locale=${v.locale})`,
          warnings
        )
      })
      return
    }
    default: {
      // Exhaustiveness guard — TypeScript will fail at compile time if a
      // new leaf primitive is added without an arm here.
      const _exhaustive: never = c
      void _exhaustive
    }
  }
}

/**
 * Emit an UNBOUND_REQUIRED_FIELD warning when `source` references a runtime
 * variable that is malformed (legacy bound with empty fieldKey, or new
 * TextValue with `{}` empty-name reference). Pure literals / 'static' values
 * are never warned — an empty literal is intentional silence; lint at the
 * value layer would be noisy.
 *
 * New-shape detection: the schema rejects `{}` at parse time, so any
 * empty-name reference can only reach this code via test fixtures that
 * bypass the schema (`as TextValue`). The lint surface still emits a warning
 * because hand-constructed templates are the lint's target audience.
 */
function checkBound(
  source: AnyValueSource,
  path: readonly string[],
  context: string,
  warnings: Warning[]
): void {
  if ('content' in source) {
    // New TextValue shape. A pure-literal content is never warned. A
    // `{name}` reference with a non-empty name is fine — the lint surface
    // delegates name resolution to the runtime / variables map (analogous to
    // legacy `bound` references where lint did not check that the data
    // record actually carries the key).
    const match = SINGLE_VARIABLE_RE.exec(source.content)
    if (match && (match[1] === undefined || match[1] === '')) {
      warnings.push({
        code: 'UNBOUND_REQUIRED_FIELD',
        severity: 'warn',
        message: `${context} references an empty variable name — it will resolve to nothing at print time.`,
        path: [...path],
        suggestion: 'Reference a non-empty variable name (e.g. `{productName}`).',
      })
    }
    return
  }
  if (source.kind === 'bound' && source.fieldKey === '') {
    warnings.push({
      code: 'UNBOUND_REQUIRED_FIELD',
      severity: 'warn',
      message: `${context} has an empty fieldKey — it will resolve to nothing at print time.`,
      path: [...path],
      suggestion: 'Set the fieldKey to a non-empty data binding.',
    })
  }
}

/**
 * Symbol-component lint. Walks the dataStructure to collect UNBOUND warnings
 * (including the per-element walk for gs1-ai), validates the symbology vs
 * structure pairing, and runs the EAN-13 / ITF-14 checksum mismatch checks.
 *
 * `lintSymbol` is split out from `lintComponent` because the symbol arm is
 * the largest — the five symbologies x four structure kinds matrix is
 * easier to follow on its own.
 */
function lintSymbol(
  zone: FlatZone,
  component: SymbolComponent,
  warnings: Warning[]
): void {
  collectBoundsFromStructure(component.dataStructure, zone.path, component, warnings)

  const ds = component.dataStructure
  switch (component.symbology) {
    case 'code-128': {
      // Code-128 prefers raw/url. gtin and gs1-ai are semantic mismatches —
      // for `gtin`, the runtime emits the digits verbatim (permissive); for
      // `gs1-ai`, GS1-128 is the right symbology.
      if (ds.kind === 'gtin' || ds.kind === 'gs1-ai') {
        warnings.push({
          code: 'SYMBOLOGY_VALIDATION_FAILED',
          severity: 'warn',
          message: `Symbol "${component.id}" symbology=code-128 with dataStructure.kind=${ds.kind} is unusual — Code-128 expects 'raw' or 'url'. ${
            ds.kind === 'gs1-ai' ? 'Use symbology=gs1-128 for GS1 AI payloads.' : ''
          }`.trim(),
          path: zone.path,
        })
      }
      break
    }
    case 'ean-13': {
      if (ds.kind === 'raw' || ds.kind === 'url') {
        warnings.push({
          code: 'SYMBOLOGY_VALIDATION_FAILED',
          severity: 'warn',
          message: `Symbol "${component.id}" symbology=ean-13 with dataStructure.kind=${ds.kind} is unusual — EAN-13 expects 'gtin'.`,
          path: zone.path,
        })
      }
      // GTIN_CHECKSUM_MISMATCH: only fires for { kind: 'gtin', checksum:
      // 'validated' } with a STATIC 13-digit payload whose check digit is
      // wrong. Bound / variable-referencing values we can't validate without
      // runtime data — skip. `auto` and `skip` modes are permissive by
      // design — skip too. "Static" here means a pure literal — either the
      // new `TextValue { content: '<literal>' }` shape (no `{var}` marker)
      // or the legacy `{ kind: 'static', value }` shape.
      if (ds.kind === 'gtin' && ds.checksum === 'validated') {
        const v = extractStaticLiteral(ds.value as AnyValueSource)
        // Only check 13-digit payloads — shorter/longer payloads might
        // intentionally be a placeholder during authoring; let runtime
        // emit-as-is and the lint surface focuses on the specific
        // "passed validation but the check digit is wrong" case.
        if (v !== undefined && /^\d{13}$/.test(v)) {
          const result = validateEan13(v)
          if (!result.ok) {
            warnings.push({
              code: 'GTIN_CHECKSUM_MISMATCH',
              severity: 'error',
              message: `Symbol "${component.id}" EAN-13 check digit mismatch: ${result.reason}.`,
              path: zone.path,
              suggestion: 'Recompute the check digit or switch checksum mode to "auto".',
            })
          }
        }
      }
      break
    }
    case 'qr': {
      // QR is permissive about payload shape (it can encode anything), but a
      // GTIN inside a QR is semantically odd — the operator probably wanted
      // either EAN-13 or to wrap the GTIN in a URL. Demoted to `info`.
      if (ds.kind === 'gtin') {
        warnings.push({
          code: 'SYMBOLOGY_VALIDATION_FAILED',
          severity: 'info',
          message: `Symbol "${component.id}" symbology=qr with dataStructure.kind=gtin is unusual — QR encodes arbitrary text; consider 'raw' or 'url'.`,
          path: zone.path,
        })
      }
      break
    }
    case 'gs1-128': {
      // GS1-128 expects gs1-ai. Everything else is tolerated permissively in
      // the runtime but is a semantic mismatch.
      if (ds.kind === 'raw' || ds.kind === 'url' || ds.kind === 'gtin') {
        warnings.push({
          code: 'SYMBOLOGY_VALIDATION_FAILED',
          severity: 'warn',
          message: `Symbol "${component.id}" symbology=gs1-128 with dataStructure.kind=${ds.kind} is unusual — GS1-128 expects 'gs1-ai'.`,
          path: zone.path,
        })
      }
      break
    }
    case 'itf': {
      // ITF-14 expects 'gtin' (like ean-13) — GTIN-14 is its only sensible
      // payload shape.
      if (ds.kind === 'raw' || ds.kind === 'url' || ds.kind === 'gs1-ai') {
        warnings.push({
          code: 'SYMBOLOGY_VALIDATION_FAILED',
          severity: 'warn',
          message: `Symbol "${component.id}" symbology=itf with dataStructure.kind=${ds.kind} is unusual — ITF-14 expects 'gtin'.`,
          path: zone.path,
        })
      }
      // GTIN_CHECKSUM_MISMATCH: only fires for { kind: 'gtin', checksum:
      // 'validated' } with a STATIC literal payload — mirrors ean-13's gate
      // (bound / variable-referencing values can't be validated without
      // runtime data; `auto`/`skip` modes stay permissive by design).
      // Unlike ean-13, a wrong LENGTH is also flagged here (not just a wrong
      // check digit): ITF-14 has no "shorter placeholder during authoring"
      // convention the way EAN-13's ≤12-digit passthrough does — GTIN-14 is
      // always exactly 14 digits, so `validateItf14` runs unconditionally
      // on whatever static literal is present.
      if (ds.kind === 'gtin' && ds.checksum === 'validated') {
        const v = extractStaticLiteral(ds.value as AnyValueSource)
        if (v !== undefined) {
          const result = validateItf14(v)
          if (!result.ok) {
            warnings.push({
              code: 'GTIN_CHECKSUM_MISMATCH',
              severity: 'error',
              message: `Symbol "${component.id}" ITF-14 validation failed: ${result.reason}.`,
              path: zone.path,
              suggestion:
                'Provide exactly 14 digits with a correct GS1 mod-10 check digit, or switch checksum mode to "auto".',
            })
          }
        }
      }
      break
    }
    case 'code-39':
    case 'ean-8':
    case 'upc-a':
    case 'datamatrix':
    case 'pdf417':
    case 'aztec-cc':
      // These symbologies aren't implemented in Phase 1 (compileSymbol
      // throws). lintTemplate is silent on them — the right place to
      // surface "this symbology isn't supported" is the runtime / Phase 1
      // gating layer, not lint.
      break
    default: {
      const _exhaustive: never = component.symbology
      void _exhaustive
    }
  }
}

/**
 * Walk a SymbolDataStructure and emit UNBOUND_REQUIRED_FIELD warnings for
 * each empty-fieldKey bound ValueSource inside it.
 */
function collectBoundsFromStructure(
  ds: SymbolDataStructure,
  path: readonly string[],
  component: SymbolComponent,
  warnings: Warning[]
): void {
  switch (ds.kind) {
    case 'raw':
    case 'gtin':
    case 'url':
      checkBound(
        ds.value,
        path,
        `symbol "${component.id}".dataStructure (kind=${ds.kind})`,
        warnings
      )
      return
    case 'gs1-ai':
      ds.elements.forEach((el, i) => {
        checkBound(
          el.value,
          path,
          `symbol "${component.id}".dataStructure.elements[${i}] (ai=${el.ai})`,
          warnings
        )
      })
      return
    default: {
      const _exhaustive: never = ds
      void _exhaustive
    }
  }
}
